import React, { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import Drawer from '../ui/Drawer'

export interface FieldDef {
  /** Key sent to the API (camelCase). */
  key: string
  /** Column read from the row when editing (snake_case). */
  rowKey: string
  label: string
  type: 'text' | 'number' | 'textarea' | 'select' | 'checkbox' | 'multi'
  required?: boolean
  /** Fixed once created: a code that other rows point at. */
  createOnly?: boolean
  options?: { value: string; label: string }[]
  /** For a select: the empty choice, e.g. "Every sport". Absent = no empty choice. */
  emptyLabel?: string
  placeholder?: string
  help?: string
  maxLength?: number
  /** For a code field: derive it from this field's value until edited by hand. */
  slugFrom?: string
  /** Uppercase as typed (country codes). */
  upper?: boolean
}

interface Props {
  isOpen: boolean
  onClose: () => void
  /** "feed provider", "placement" … */
  noun: string
  fields: FieldDef[]
  /** The row being edited; null to add a new one. */
  row: Record<string, any> | null
  onSave: (values: Record<string, unknown>, isNew: boolean) => Promise<void>
}

const CODE_PATTERN = /^[a-z0-9_]+$/
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 50)

const inputCls = 'w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:text-gray-500'

/**
 * Add or edit one reference row, driven by a field list per tab.
 *
 * Blank optional fields are sent as null so a cleared value really clears.
 * Create-only fields (codes) show read-only when editing: detections,
 * broadcasts and rate cards point at them.
 */
const ReferenceEditDrawer: React.FC<Props> = ({ isOpen, onClose, noun, fields, row, onSave }) => {
  const isNew = row === null
  const [values, setValues] = useState<Record<string, any>>({})
  const [touched, setTouched] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    const v: Record<string, any> = {}
    for (const f of fields) {
      const raw = row?.[f.rowKey]
      if (f.type === 'checkbox') v[f.key] = !!raw
      else if (f.type === 'multi') v[f.key] = Array.isArray(raw) ? [...raw] : []
      else v[f.key] = raw == null ? '' : String(raw)
    }
    setValues(v)
    setTouched(new Set())
    setError(null)
    // Not on `fields`: the page rebuilds that list every render, and resetting
    // on it would wipe what was typed at each keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, row])

  const set = (key: string, value: any) => {
    setValues((prev) => {
      const next = { ...prev, [key]: value }
      // Codes follow their source field until someone types in them.
      if (isNew) {
        for (const f of fields) {
          if (f.slugFrom === key && !touched.has(f.key)) next[f.key] = slug(String(value))
        }
      }
      return next
    })
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const out: Record<string, unknown> = {}
    for (const f of fields) {
      if (f.createOnly && !isNew) continue
      const v = values[f.key]
      if (f.type === 'checkbox' || f.type === 'multi') { out[f.key] = v; continue }
      const s = String(v ?? '').trim()
      if (f.required && !s) { setError(`${f.label} is required`); return }
      if (f.key === 'code' && s && !CODE_PATTERN.test(s)) {
        setError('Code may use only lowercase letters, numbers and underscores'); return
      }
      if (f.type === 'number') {
        if (s === '') { if (!isNew) continue; out[f.key] = undefined; continue }
        const n = Number(s)
        if (!Number.isInteger(n) || n < 0) { setError(`${f.label} must be a whole number`); return }
        out[f.key] = n
      } else {
        out[f.key] = s === '' ? null : s
      }
    }
    setError(null)
    setSaving(true)
    try {
      await onSave(out, isNew)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} width="md"
            title={isNew ? `Add ${noun}` : `Edit ${noun}`}
            description="Platform-wide: this applies to every customer.">
      <form onSubmit={submit} className="space-y-4">
        {fields.map((f) => {
          const locked = !!f.createOnly && !isNew
          const id = `ref-${f.key}`
          return (
            <div key={f.key}>
              {f.type !== 'checkbox' && (
                <label htmlFor={id} className="block text-xs font-medium text-gray-700 mb-1">
                  {f.label}{f.required && !locked && <span className="text-red-500"> *</span>}
                </label>
              )}
              {f.type === 'textarea' ? (
                <textarea id={id} rows={4} className={inputCls} maxLength={f.maxLength}
                          placeholder={f.placeholder} value={values[f.key] ?? ''}
                          onChange={(e) => set(f.key, e.target.value)} />
              ) : f.type === 'select' ? (
                <select id={id} className={inputCls} value={values[f.key] ?? ''} disabled={locked}
                        onChange={(e) => set(f.key, e.target.value)}>
                  {f.emptyLabel !== undefined
                    ? <option value="">{f.emptyLabel}</option>
                    : (values[f.key] ? null : <option value="" disabled>Select…</option>)}
                  {(f.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : f.type === 'checkbox' ? (
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input type="checkbox" checked={!!values[f.key]}
                         onChange={(e) => set(f.key, e.target.checked)}
                         className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  {f.label}
                </label>
              ) : f.type === 'multi' ? (
                <div className="grid grid-cols-2 gap-1.5 border border-gray-200 rounded-lg p-2.5 max-h-48 overflow-y-auto">
                  {(f.options ?? []).map((o) => {
                    const on = (values[f.key] ?? []).includes(o.value)
                    return (
                      <label key={o.value} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" checked={on}
                               onChange={() => set(f.key, on
                                 ? values[f.key].filter((x: string) => x !== o.value)
                                 : [...(values[f.key] ?? []), o.value])}
                               className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        {o.label}
                      </label>
                    )
                  })}
                </div>
              ) : (
                <input id={id} type={f.type === 'number' ? 'number' : 'text'} className={inputCls}
                       disabled={locked} maxLength={f.maxLength} placeholder={f.placeholder}
                       value={values[f.key] ?? ''}
                       onChange={(e) => {
                         if (f.slugFrom) setTouched((t) => new Set(t).add(f.key))
                         set(f.key, f.upper ? e.target.value.toUpperCase() : e.target.value)
                       }} />
              )}
              {(f.help || locked) && (
                <p className="text-[11px] text-gray-500 mt-1">
                  {locked ? 'Fixed once created — other records point at it.' : f.help}
                </p>
              )}
            </div>
          )
        })}

        {error && <p className="text-xs text-red-600 bg-red-50 rounded px-2.5 py-1.5">{error}</p>}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose}
                  className="flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={saving}
                  className="flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50">
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {isNew ? `Add ${noun}` : 'Save'}
          </button>
        </div>
      </form>
    </Drawer>
  )
}

export default ReferenceEditDrawer
